// src/components/features/departures/DepartureItem/DepartureItem.tsx
import type { ComponentProps } from 'preact';
import type { Visit, VisitRestriction } from '../../../../arcticapi/types';
import { DisplayTime } from '../DisplayTime/DisplayTime';
import { LineRoundel } from '../../../base/LineRoundel/LineRoundel';
import { SvgIcon } from '../../../base/Icon/SvgIcon';
import { SvgName } from '../../../base/Icon/SvgRegistry';
import { Chip } from '../../../base/Chip/Chip';

// styles
import styles from './Departure.module.css';

export interface DepartureModel extends Pick<
  Visit,
  | 'destinationName'
  | 'displayTime'
  | 'expectedDepartureTime'
  | 'aimedDepartureTime'
  | 'isRealTime'
  | 'line'
> {
  id: string;
  restriction?: VisitRestriction;
}

export interface DepartureProps extends Omit<
  ComponentProps<'li'>,
  'children' | 'className'
> {
  departure: DepartureModel;
  paused?: boolean;
}

function getDepartureTime({
  displayTime,
  expectedDepartureTime,
  aimedDepartureTime,
}: DepartureModel) {
  return displayTime ?? expectedDepartureTime ?? aimedDepartureTime ?? 'N/A';
}

function getRestrictionConfig(restriction: DepartureModel['restriction']) {
  switch (restriction) {
    case 'pickUpOnly':
      return { label: 'Pick up only', iconName: 'pickUpOnly' as const };
    case 'setDownOnly':
      return { label: 'Set down only', iconName: 'setDownOnly' as const };
    default:
      return null;
  }
}

/**
 * Represents a single departure entry within a departure list
 *
 * Intended for line roundel, destination, and expected time
 */
export function Departure({
  departure,
  paused = false,
  ...props
}: DepartureProps) {
  const showIcon = paused || departure.isRealTime;
  const restrictionConfig = getRestrictionConfig(departure.restriction);

  const pausedIconConfig = departure.expectedDepartureTime
    ? {
        name: 'rtiPaused',
        size: 24,
        color: '#595959',
      }
    : {
        name: 'pause',
        size: 24,
        color: '#595959',
      };

  const iconConfig = paused
    ? pausedIconConfig
    : {
        name: 'rti',
        size: 16,
        color: '#ffffff',
      };

  return (
    <li className={styles.row} {...props}>
      <div className={styles.details}>
        <div className={styles.main}>
          {departure.line && <LineRoundel line={departure.line} />}
          <span className={styles.destination}>
            {departure.destinationName}
          </span>
          {restrictionConfig ? (
            <Chip
              aria-label={restrictionConfig.label}
              icon={<SvgIcon name={restrictionConfig.iconName} size={16} />}
            >
              {restrictionConfig.label}
            </Chip>
          ) : null}
        </div>

        <div className={styles.displayTimeContainer}>
          <DisplayTime
            className={styles.time}
            text={getDepartureTime(departure)}
            isRealTime={departure.isRealTime}
            paused={paused}
          />
          {showIcon ? (
            <div
              className={`${styles.iconContainer} ${!paused ? styles.animate : ''}`}
            >
              <SvgIcon
                name={iconConfig.name as SvgName}
                size={iconConfig.size}
                fill={iconConfig.color}
                className={!paused ? styles.animate : ''}
              />
            </div>
          ) : (
            <div className={styles.reserveIconContainer}></div>
          )}
        </div>
      </div>
    </li>
  );
}
